import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import { Logo, getPublisherLogos, uploadLogo, updateLogoMetadata, deleteLogo, setDefaultLogo, getDefaultLogo, setLogoDisplayMode, getLogoUrlWithCacheBusting } from '../../api/logoApi';
import UploadLogoModal from './UploadLogoModal';
import SectionTitle from '../profile/sheet/shared/SectionTitle';
import EditLogoModal from './EditLogoNameModal';

type DisplayMode = 'icon_only' | 'name_only' | 'icon_and_name';

export interface LogoSelectorHandle {
  open: () => void;
}

interface LogoSelectorProps {
  brandId?: number;  // Brand ID (formerly publisherId)
  publisherId?: number;  // Deprecated: Use brandId instead
  brandName?: string;
  legacyLogoUrl?: string;
  defaultLogo?: Logo | null;  // Pre-loaded default logo from parent
  logoDisplayMode?: DisplayMode;  // Current display mode from backend
  canEdit?: boolean;
  openOnClick?: boolean;  // Whether clicking the logo opens the dropdown (default true)
  onChange?: (logo: Logo | null, displayMode: DisplayMode) => void;
  skipBrandUpdate?: boolean;  // Skip updating brand default (for content item logo selection)
}

const LogoSelector = forwardRef<LogoSelectorHandle, LogoSelectorProps>(({
  brandId,
  publisherId,  // Backward compatibility
  brandName = 'Brand',
  legacyLogoUrl,
  defaultLogo,
  logoDisplayMode = 'icon_and_name',
  canEdit = false,
  openOnClick = true,
  onChange,
  skipBrandUpdate = false
}, ref) => {
  // Use brandId if available, fallback to publisherId for backward compatibility
  const effectiveBrandId = brandId || publisherId;
  const [isOpen, setIsOpen] = useState(false);
  const [logos, setLogos] = useState<Logo[]>([]);
  const [selectedLogo, setSelectedLogo] = useState<Logo | null>(null);
  const [currentDisplayMode, setCurrentDisplayMode] = useState<DisplayMode>(logoDisplayMode);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingIconType, setUploadingIconType] = useState<'icon_white' | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLogo, setEditingLogo] = useState<Logo | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingLogoId, setDeletingLogoId] = useState<number | null>(null);
  const [hoveredIcon, setHoveredIcon] = useState<DisplayMode | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  /** Preview surface for the logo tiles — LoD/DoL, Design-section parity. */
  const [logoPreviewBg, setLogoPreviewBg] = useState<'dark' | 'light'>('dark');

  // Load logos from API when effectiveBrandId is available
  useEffect(() => {
    if (effectiveBrandId) {
      // If defaultLogo is a custom logo (not icon_white), it takes precedence over display modes
      // This ensures custom logo variants persist correctly on refresh
      if (defaultLogo && defaultLogo.logoKey !== 'icon_white') {
        // Custom logo variant selected - use it regardless of display mode
        setSelectedLogo(defaultLogo);
        setLoading(false);
        // Still load full logo list for dropdown (now cached to prevent duplicate requests)
        loadLogosOnly();
      } else {
        // Using standard display modes (icon_only, name_only, icon_and_name)
        // These modes should NOT have a selected logo
        setSelectedLogo(null);

        // If defaultLogo is icon_white, we can render immediately
        // Otherwise wait for logos to load
        if (defaultLogo?.logoKey === 'icon_white') {
          setLoading(false);
        }

        // Load logos which will set loading=false when done (now cached)
        loadLogosOnly();
      }
    }
    // defaultLogo?.url in the deps because callers (content card, template
    // preview) pass SYNTHESIZED Logo objects with no id — swapping one
    // synthetic logo for another kept id undefined→undefined and this
    // effect never re-fired, so the trigger kept painting the old logo
    // until a full remount (e.g. page refresh).
  }, [effectiveBrandId, defaultLogo?.id, defaultLogo?.url, logoDisplayMode]);

  // Update display mode when prop changes
  useEffect(() => {
    setCurrentDisplayMode(logoDisplayMode);
  }, [logoDisplayMode]);

  const loadLogosOnly = async () => {
    if (!effectiveBrandId) return;

    try {
      const fetchedLogos = await getPublisherLogos(effectiveBrandId);
      setLogos(fetchedLogos);
      // Always set loading false after logos are loaded, regardless of mode
      setLoading(false);
    } catch (error) {
      console.error('[LogoSelector] Failed to load brand logos:', error);
      setLoading(false); // Set loading false even on error
    }
  };

  const loadLogosAndDefault = async () => {
    if (!effectiveBrandId) return;

    try {
      setLoading(true);

      // Fetch both logos and default logo in parallel
      const [fetchedLogos, fetchedDefaultLogo] = await Promise.all([
        getPublisherLogos(effectiveBrandId),
        getDefaultLogo(effectiveBrandId).catch(() => null) // Handle 404 gracefully
      ]);

      setLogos(fetchedLogos);

      // Set selected logo in priority order
      if (fetchedDefaultLogo) {
        setSelectedLogo(fetchedDefaultLogo);
      } else if (fetchedLogos.length > 0) {
        // Fallback to first logo
        setSelectedLogo(fetchedLogos[0]);
      }
    } catch (error) {
      console.error('[LogoSelector] Failed to load publisher logos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogos = async () => {
    if (!effectiveBrandId) return;

    try {
      const fetchedLogos = await getPublisherLogos(effectiveBrandId);
      setLogos(fetchedLogos);
    } catch (error) {
      console.error('[LogoSelector] Failed to load publisher logos:', error);
    }
  };

  // Calculate dropdown position based on viewport
  const calculateDropdownPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Header context: sibling dropdown standard — 34px drop on desktop,
    // full header width below the pill at <=640, viewport-clamped height.
    // Elsewhere (content cards, legacy header) keep the generic anchored flip.
    const header = triggerRef.current.closest('.tend-page-header');
    if (header) {
      const mobile = window.innerWidth <= 640;
      const top = mobile
        ? header.getBoundingClientRect().bottom + 12
        : rect.bottom + 34;
      setDropdownPosition('below');
      setDropdownStyle({
        position: 'fixed',
        ...(mobile
          ? { left: 12, right: 12, width: 'auto' }
          : { left: `${rect.left}px` }),
        top: `${top}px`,
        maxHeight: Math.max(320, viewportHeight - top - 24),
      });
      return;
    }

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If not enough space below (less than 400px) and more space above, show above
    if (spaceBelow < 400 && spaceAbove > spaceBelow) {
      setDropdownPosition('above');
      setDropdownStyle({
        position: 'fixed',
        left: `${rect.left}px`,
        bottom: `${viewportHeight - rect.top}px`,
        minWidth: '320px',
        maxWidth: '95vw'
      });
    } else {
      setDropdownPosition('below');
      setDropdownStyle({
        position: 'fixed',
        left: `${rect.left}px`,
        top: `${rect.bottom + 8}px`,
        minWidth: '320px',
        maxWidth: '95vw'
      });
    }
  };

  // Reposition while open (sibling standard): follow the trigger through
  // resizes and page scrolls — a stale fixed position drifts off the pill.
  useEffect(() => {
    if (!isOpen) return;
    calculateDropdownPosition();
    window.addEventListener('resize', calculateDropdownPosition);
    window.addEventListener('scroll', calculateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', calculateDropdownPosition);
      window.removeEventListener('scroll', calculateDropdownPosition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Expose open() to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => {
      calculateDropdownPosition();
      setIsOpen(true);
    }
  }));

  // Click-only open (hover delay removed)
  const handleMouseEnter = () => {};
  const handleMouseLeave = () => {};

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle display mode selection
  const handleDisplayModeSelect = async (mode: DisplayMode) => {
    if (!effectiveBrandId) return;

    // Save current state for potential rollback
    const previousMode = currentDisplayMode;
    const previousLogo = selectedLogo;

    try {
      // Update UI immediately (optimistic update)
      setCurrentDisplayMode(mode);
      setSelectedLogo(null); // Clear custom logo selection immediately
      setIsOpen(false);

      // Only update brand default if not in content item mode
      if (!skipBrandUpdate) {
        // Save display mode to backend for brand
        await setLogoDisplayMode(effectiveBrandId, mode);
      }

      // Notify parent component (parent will handle content item update if needed)
      onChange?.(null, mode);
    } catch (error) {
      console.error('[LogoSelector] Failed to set display mode:', error);
      // Revert UI to previous state on error
      setCurrentDisplayMode(previousMode);
      setSelectedLogo(previousLogo);
    }
  };

  // Handle logo selection
  const handleLogoSelect = async (logo: Logo | 'icon_white') => {
    if (!effectiveBrandId) return;

    // If selecting a logo slot that doesn't exist, open upload modal
    if (logo === 'icon_white') {
      setUploadingIconType(logo);
      setShowUploadModal(true);
      setIsOpen(false);
      return;
    }

    try {
      // Update UI immediately
      setSelectedLogo(logo);
      setCurrentDisplayMode('icon_and_name'); // Clear display mode when custom logo selected
      setIsOpen(false);

      // Only update brand default if not in content item mode
      if (!skipBrandUpdate) {
        await setDefaultLogo(effectiveBrandId, logo.id);
      }

      // Notify parent component (parent will handle content item update if needed)
      onChange?.(logo, 'icon_and_name');
    } catch (error) {
      console.error('Failed to set default logo:', error);
    }
  };

  // Handle logo upload
  const handleUpload = async (file: File, displayName: string, logoKey: string) => {
    if (!effectiveBrandId) return;

    try {
      setLoading(true);
      await uploadLogo(effectiveBrandId, file, displayName, logoKey);

      // Reload both logos and default logo to ensure UI is in sync
      await loadLogosAndDefault();

      setShowUploadModal(false);
      setUploadingIconType(null);

      // If dropdown was open before upload, refresh it by reopening
      // This ensures the new logo appears immediately in the dropdown
      if (canEdit) {
        // Small delay to ensure state updates have propagated
        setTimeout(() => {
          calculateDropdownPosition();
          setIsOpen(true);
        }, 100);
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle logo name edit
  const handleEditName = (logo: Logo) => {
    setEditingLogo(logo);
    setShowEditModal(true);
    setIsOpen(false);
  };

  const handleSaveLogoChanges = async (
    logoId: number,
    updates: {
      file?: File;
      displayName?: string;
      logoKey?: string;
    }
  ) => {
    try {
      // If file is provided, upload it (which will also update metadata)
      if (updates.file && effectiveBrandId) {
        const logoToUpdate = logos.find(l => l.id === logoId);
        if (logoToUpdate) {
          // Upload with new or existing logo key and display name
          const finalLogoKey = updates.logoKey || logoToUpdate.logoKey || 'custom';
          const finalDisplayName = updates.displayName || logoToUpdate.displayName;

          await uploadLogo(
            effectiveBrandId,
            updates.file,
            finalDisplayName,
            finalLogoKey
          );
        }
      } else {
        // No file - update metadata only
        const metadataUpdates: { displayName?: string; logoKey?: string } = {};

        if (updates.displayName) {
          metadataUpdates.displayName = updates.displayName;
        }

        if (updates.logoKey) {
          metadataUpdates.logoKey = updates.logoKey;
        }

        // Only call API if there are actual metadata changes
        if (Object.keys(metadataUpdates).length > 0) {
          await updateLogoMetadata(logoId, metadataUpdates);
        }
      }

      // Reload logos to show updates
      await loadLogos();

      setShowEditModal(false);
      setEditingLogo(null);
    } catch (error) {
      console.error('Failed to update logo:', error);
      throw error;
    }
  };

  // Handle logo deletion - show confirmation
  const handleDeleteClick = (logoId: number) => {
    setDeletingLogoId(logoId);
  };

  // Confirm logo deletion
  const confirmDelete = async (logoId: number) => {
    try {
      await deleteLogo(logoId);
      await loadLogos();

      // If deleted logo was selected, clear selection
      if (selectedLogo?.id === logoId) {
        setSelectedLogo(null);
      }

      setDeletingLogoId(null);
    } catch (error) {
      console.error('Failed to delete logo:', error);
      alert('Failed to delete logo.');
      setDeletingLogoId(null);
    }
  };

  // Cancel logo deletion
  const cancelDelete = () => {
    setDeletingLogoId(null);
  };

  // Get icon logo and custom variants
  // Use icon_white from logos array, or fall back to defaultLogo if it's icon_white
  const iconWhite = logos.find(logo => logo.logoKey === 'icon_white') ||
    (defaultLogo?.logoKey === 'icon_white' ? defaultLogo : null);
  const customLogos = logos.filter(logo => !logo.logoKey?.startsWith('icon_'));

  // Get first letter of brand name for fallback
  const brandInitial = brandName.charAt(0).toUpperCase();

  // Determine if using display mode (no custom logo selected)
  const isUsingDisplayMode = !selectedLogo;

  // No effectiveBrandId - don't render
  if (!effectiveBrandId) {
    console.warn('🎨 [LogoSelector] No effectiveBrandId - not rendering');
    return null;
  }

  return (
    <>
      <div className="relative" ref={triggerRef}>
        {/* Header Display - Logo/Icon + Brand Name */}
        {!loading && (
          <div
            className={`flex items-center gap-3 ${canEdit ? 'cursor-pointer' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
              if (canEdit && openOnClick) {
                e.stopPropagation(); // Prevent bubbling to card
                calculateDropdownPosition();
                setIsOpen(true);
              }
            }}
          >
            {selectedLogo ? (
              // Custom logo selected - show full wordmark
              <>
                <img
                  src={selectedLogo.url}
                  alt={selectedLogo.displayName}
                  className={`h-8 w-auto object-contain max-w-[120px] ${selectedLogo.logoKey === 'icon_white' ? 'rounded-[10px]' : ''
                    }`}
                />
                {/* Only show brand name text for icon_white, not for full wordmark variants */}
                {selectedLogo.logoKey === 'icon_white' && (
                  <span className="text-lg font-semibold text-white">{brandName}</span>
                )}
              </>
            ) : (
              // No custom logo - use display mode
              <>
                {(currentDisplayMode === 'icon_only' || currentDisplayMode === 'icon_and_name') && (
                  iconWhite ? (
                    <img
                      src={iconWhite.url}
                      alt={iconWhite.displayName}
                      className="h-8 w-auto object-contain max-w-[48px] rounded-[10px]"
                    />
                  ) : (
                    <div className="h-8 w-8 bg-violet-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                      {brandInitial}
                    </div>
                  )
                )}
                {(currentDisplayMode === 'name_only' || currentDisplayMode === 'icon_and_name') && (
                  <span className="text-lg font-semibold text-white">{brandName}</span>
                )}
              </>
            )}
          </div>
        )}

        {/* Dropdown Menu — the Design panel's Logos section, force-stacked */}
        {isOpen && canEdit && createPortal(
          (() => {
            const onLight = logoPreviewBg === 'light';
            const tileBgOverride: React.CSSProperties = onLight
              ? { background: 'rgba(20, 26, 40, .06)' }
              : {};
            const tileMicroColor = onLight ? { color: 'rgba(27, 31, 42, .55)' } : undefined;
            const nameSpan = (
              <span
                style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  color: onLight ? 'var(--text-ink)' : 'var(--text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {brandName}
              </span>
            );
            const iconThumb = iconWhite ? (
              <img
                src={getLogoUrlWithCacheBusting(iconWhite)}
                alt=""
                style={{ height: 28, width: 'auto', maxWidth: 48, objectFit: 'contain', borderRadius: 8 }}
              />
            ) : (
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,.92)',
                  color: '#1c2740',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: "'Inter Tight', sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {brandInitial}
              </span>
            );
            const editableIcon = (hoverKey: DisplayMode) => (
              <span
                style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
                onMouseEnter={() => setHoveredIcon(hoverKey)}
                onMouseLeave={() => setHoveredIcon(null)}
              >
                {iconThumb}
                {hoveredIcon === hoverKey && (
                  <span
                    role="button"
                    title="Replace brand icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadingIconType('icon_white');
                      setShowUploadModal(true);
                      setIsOpen(false);
                    }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,.6)',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </span>
                )}
              </span>
            );
            return (
          <div
            ref={dropdownRef}
            data-overlay-popover=""
            className="tend-menu tend-surface tend-f2"
            style={{
              width: 400,
              zIndex: Z.PICKER,
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              // Spread LAST so the position calc can override width (mobile
              // full-header insets) and maxHeight (viewport clamp).
              ...dropdownStyle,
            }}
          >
            <section className="tend-section-card" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <SectionTitle
                meta={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      role="tablist"
                      aria-label="Logo preview background"
                      style={{
                        position: 'relative',
                        display: 'flex',
                        borderRadius: 999,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,.30)',
                        flexShrink: 0,
                      }}
                    >
                      {([
                        { value: 'dark' as const, label: 'LoD', bg: '#141a28', color: '#fff', clip: 'polygon(0 0, 100% 0, calc(100% - 12px) 100%, 0 100%)', mr: -12, pad: '6px 18px 6px 14px' },
                        { value: 'light' as const, label: 'DoL', bg: '#f4f6fb', color: 'var(--text-ink)', clip: 'polygon(12px 0, 100% 0, 100% 100%, 0 100%)', mr: 0, pad: '6px 14px 6px 18px' },
                      ]).map(({ value, label, bg, color, clip, mr, pad }) => {
                        const active = logoPreviewBg === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setLogoPreviewBg(value)}
                            style={{
                              border: 0,
                              cursor: 'pointer',
                              width: 54,
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 10,
                              letterSpacing: 1,
                              fontWeight: active ? 700 : 500,
                              opacity: active ? 1 : 0.35,
                              boxShadow: active ? 'inset 0 -2px 0 var(--accent-blue)' : 'none',
                              background: bg,
                              color,
                              clipPath: clip,
                              marginRight: mr,
                              padding: pad,
                              transition: 'opacity .2s, box-shadow .2s',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: -3,
                          bottom: -3,
                          left: '50%',
                          width: 2,
                          background: 'rgba(255,255,255,.55)',
                          transform: 'translateX(-50%) rotate(26deg)',
                          pointerEvents: 'none',
                        }}
                      />
                    </span>
                    <button
                      type="button"
                      className="tend-btn tend-btn--commit"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      onClick={() => {
                        setIsOpen(false);
                        setUploadingIconType(null);
                        setShowUploadModal(true);
                      }}
                    >
                      Add Logo
                    </button>
                  </span>
                }
              >
                Logos
              </SectionTitle>

              <div
                className="tend-scroll"
                style={{
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  borderRadius: 12,
                  padding: onLight ? 10 : 0,
                  background: onLight ? '#f4f6fb' : 'transparent',
                  transition: 'background .25s',
                }}
              >
                {/* Display modes — ring selection, no radios */}
                <button
                  type="button"
                  className={`tend-logo-tile${isUsingDisplayMode && currentDisplayMode === 'icon_only' ? ' is-selected' : ''}`}
                  style={tileBgOverride}
                  onClick={() => handleDisplayModeSelect('icon_only')}
                >
                  {editableIcon('icon_only')}
                  <span className="tend-micro" style={{ marginLeft: 'auto', ...tileMicroColor }}>Icon</span>
                </button>
                <button
                  type="button"
                  className={`tend-logo-tile${isUsingDisplayMode && currentDisplayMode === 'name_only' ? ' is-selected' : ''}`}
                  style={tileBgOverride}
                  onClick={() => handleDisplayModeSelect('name_only')}
                >
                  {nameSpan}
                  <span className="tend-micro" style={{ marginLeft: 'auto', ...tileMicroColor }}>Name</span>
                </button>
                <button
                  type="button"
                  className={`tend-logo-tile${isUsingDisplayMode && currentDisplayMode === 'icon_and_name' ? ' is-selected' : ''}`}
                  style={tileBgOverride}
                  onClick={() => handleDisplayModeSelect('icon_and_name')}
                >
                  {editableIcon('icon_and_name')}
                  {nameSpan}
                  <span className="tend-micro" style={{ marginLeft: 'auto', ...tileMicroColor }}>Icon + Name</span>
                </button>

                {/* Variants — stacked, just the logos */}
                {customLogos.map((logo) => {
                  const confirming = deletingLogoId === logo.id;
                  const selected = selectedLogo?.id === logo.id;
                  return (
                    <div key={logo.id} className="tend-hover-host">
                      <button
                        type="button"
                        className={`tend-logo-tile${selected ? ' is-selected' : ' tend-logo-tile--bare'}`}
                        style={{ width: '100%', padding: 10, ...(selected ? tileBgOverride : {}) }}
                        title={logo.displayName}
                        onClick={() => handleLogoSelect(logo)}
                      >
                        <img
                          src={getLogoUrlWithCacheBusting(logo)}
                          alt={logo.displayName}
                          style={{ height: 44, width: 'auto', maxWidth: 220, objectFit: 'contain' }}
                        />
                      </button>
                      {confirming ? (
                        <span className="tend-hover-actions is-pinned">
                          <button
                            type="button"
                            className="tend-hover-action tend-hover-action--danger"
                            title="Confirm remove"
                            onClick={() => confirmDelete(logo.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="tend-hover-action"
                            title="Cancel"
                            onClick={cancelDelete}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                            </svg>
                          </button>
                        </span>
                      ) : (
                        <span className="tend-hover-actions">
                          <button
                            type="button"
                            className="tend-hover-action tend-hover-action--edit"
                            title="Edit logo"
                            onClick={() => handleEditName(logo)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="tend-hover-action tend-hover-action--danger"
                            title="Delete logo"
                            onClick={() => handleDeleteClick(logo.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
            );
          })(),
          document.body
        )}
      </div>

      {/* Upload Modal */}
      <UploadLogoModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadingIconType(null);
        }}
        onUpload={handleUpload}
        uploadingIconType={uploadingIconType}
      />

      {/* Edit Logo Modal */}
      {editingLogo && (
        <EditLogoModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingLogo(null);
          }}
          logo={editingLogo}
          onSave={handleSaveLogoChanges}
        />
      )}
    </>
  );
});

// Memoize component to prevent re-renders when parent re-renders but props haven't changed
export default React.memo(LogoSelector);
